// Shopify cart integration for the prototype bag drawer.
// Replaces the prototype's localStorage demo with real Shopify cart state.
// Every cart call checks res.ok: an error response must never be rendered
// as an empty bag, and a failed add must never fail silently.

const drawer = document.getElementById('bagDrawer');
const scrim = document.getElementById('bagDrawerScrim');
const drawerBtn = document.getElementById('navBagBtn');
const drawerClose = document.getElementById('bagDrawerClose');
const drawerItems = document.getElementById('bagDrawerItems');
const drawerSub = document.getElementById('bagDrawerSub');
const drawerFooter = document.getElementById('bagDrawerFooter');
const drawerTotal = document.getElementById('bagDrawerTotal');
const navBagCount = document.getElementById('navBagCount');
const navBag = document.getElementById('navBagBtn');

function escapeHtml(value) {
  if (value == null) return '';
  return String(value).replace(/[&<>"']/g, ch => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;'
  }[ch]));
}

function formatMoney(cents) {
  const n = (cents || 0) / 100;
  const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0
    }).format(n);
  } catch (e) {
    return '$' + n.toLocaleString();
  }
}

async function fetchCart() {
  const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
  if (!res.ok) throw new Error('The bag could not be loaded. Try again.');
  return res.json();
}

async function changeLine(lineKey, quantity) {
  const res = await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ id: lineKey, quantity: quantity })
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.description || err.message || 'The bag could not be updated. Try again.');
  }
  return res.json();
}

function renderCart(cart) {
  const count = cart.item_count || 0;
  if (navBagCount) navBagCount.textContent = String(count);
  if (navBag) navBag.classList.toggle('has-items', count > 0);

  if (!drawerItems) return;

  if (count === 0) {
    drawerItems.innerHTML = '';
    if (drawerSub) drawerSub.textContent = 'Empty. The collection awaits.';
    if (drawerFooter) drawerFooter.hidden = true;
    return;
  }

  drawerItems.innerHTML = cart.items.map(item => {
    const isBrother = item.handle === 'brother' || (item.product_title || '').indexOf("Brother's Keeper") !== -1;
    const image = isBrother && window.AA_BROTHERS_GIF
      ? window.AA_BROTHERS_GIF
      : (item.image ? item.image.replace('{width}', '200') : '');
    const url = item.url || '/cart';
    return `
      <div class="bag-item" data-key="${escapeHtml(item.key)}">
        <div class="bag-item-thumb"><img src="${escapeHtml(image)}" alt="${escapeHtml(item.product_title)}"></div>
        <div class="bag-item-info">
          <a href="${escapeHtml(url)}" class="bag-item-title">${escapeHtml(item.product_title)}</a>
          <div class="bag-item-price">${formatMoney(item.final_line_price)}</div>
        </div>
        <button class="bag-item-remove" data-remove="${escapeHtml(item.key)}" aria-label="Remove">×</button>
      </div>
    `;
  }).join('');

  if (drawerSub) {
    drawerSub.textContent = `${count} work${count === 1 ? '' : 's'} held in the archive.`;
  }
  if (drawerFooter) {
    drawerFooter.hidden = false;
    if (drawerTotal) drawerTotal.textContent = formatMoney(cart.total_price);
  }
}

function showDrawerNote(message) {
  if (drawerSub) drawerSub.textContent = message;
}

async function refresh(opts) {
  // A cart change reflected into an OPEN drawer must re-sync the accelerated wallets,
  // or an Acquire/quick-add that resolves while the drawer is open leaves a wallet
  // bound to the pre-change cart. Clear it NOW, before the await: during a slow (or
  // failed) /cart.js the old wallet must not stay tappable. A normal openDrawer relies
  // on this; the syncWallets:false escape hatch is for callers that render it themselves.
  const syncWallets = (!opts || opts.syncWallets !== false) && drawer && drawer.classList.contains('is-open');
  if (syncWallets) clearDrawerWallets();
  try {
    const cart = await fetchCart();
    renderCart(cart);
    window.dispatchEvent(new CustomEvent('bag:change', { detail: { cart } }));
    if (syncWallets) renderDrawerWallets();
    return true;
  } catch (err) {
    // Leave the cart contents alone (an error is not an empty bag), but the wallet
    // stays cleared from above so no stale button survives a failed refresh.
    console.error('[bag]', err);
    showDrawerNote('The bag could not be refreshed. Try again.');
    return false;
  }
}

// Re-render the drawer's accelerated-checkout wallets against the LIVE cart.
// content_for_additional_checkout_buttons renders all enabled wallets (Apple Pay /
// Google Pay / Shop Pay) bound to the cart; Shopify's own guidance is to re-render
// them via the Section Rendering API when the drawer opens or the cart changes, so
// the Apple Pay sheet reflects the real contents. Token-guarded: open + each removal
// can race two renders, and a late response must never restore a stale wallet.
// Express is a bonus path — any failure here is swallowed so it can't break the drawer.
let drawerWalletToken = 0;
// Invalidate any in-flight wallet render and remove the current wallet immediately,
// so a button bound to a since-changed cart can never stay tappable. Returns the
// fresh token. Call this BEFORE any async cart work that will re-render the wallet
// (and on failure paths) — clearing only AFTER the cart fetch leaves a tappable
// stale wallet during a slow/failed /cart.js.
function clearDrawerWallets() {
  const host = document.getElementById('bagDrawerWallets');
  if (host) host.innerHTML = '';
  return ++drawerWalletToken;
}
async function renderDrawerWallets() {
  const host = document.getElementById('bagDrawerWallets');
  if (!host) return;
  // Clear FIRST: a wallet rendered against an older cart must never be tappable
  // while the fresh one is in flight (the Apple Pay sheet would preview the wrong
  // cart). The host stays empty until — and unless — a fresh wallet node lands.
  const token = clearDrawerWallets();
  try {
    const res = await fetch('/?sections=header', { headers: { 'Accept': 'application/json' } });
    if (!res.ok || token !== drawerWalletToken) return;
    const data = await res.json();
    if (!data || !data.header || token !== drawerWalletToken) return;
    const fresh = new DOMParser().parseFromString(data.header, 'text/html').getElementById('bagDrawerWallets');
    if (token !== drawerWalletToken) return;
    // No fresh node (wallets disabled / empty section) → leave the host cleared.
    if (!fresh) return;
    host.innerHTML = fresh.innerHTML;
    if (window.Shopify && Shopify.PaymentButton && typeof Shopify.PaymentButton.init === 'function') {
      Shopify.PaymentButton.init();
    }
  } catch (e) {
    /* express is a bonus path; the host is already cleared, never leave it stale */
  }
}

function openDrawer(opts) {
  if (!drawer) return;
  // Already open: return before locking. AAScrollLock is refcounted, so a second
  // open (a stale acquire continuation resolving after the buyer retried, say)
  // took a second lock that the single visible close could never release, and
  // the page stayed pinned with no modal left to dismiss.
  if (drawer.classList.contains('is-open')) return;
  drawer.classList.add('is-open');
  if (scrim) scrim.classList.add('is-active');
  drawer.setAttribute('aria-hidden', 'false');
  window.AAScrollLock?.lock();
  // The drawer is now open. Either path renders the wallets exactly once: a normal
  // open lets refresh() sync them (drawer is open); the {refresh:false} path (the
  // caller already refreshed while the drawer was closed) renders them directly.
  if (!opts || opts.refresh !== false) {
    refresh();
  } else {
    renderDrawerWallets();
  }
}
function closeDrawer() {
  if (!drawer || !drawer.classList.contains('is-open')) return;
  drawer.classList.remove('is-open');
  if (scrim) scrim.classList.remove('is-active');
  drawer.setAttribute('aria-hidden', 'true');
  window.AAScrollLock?.unlock();
}

if (drawerBtn) drawerBtn.addEventListener('click', (e) => { e.preventDefault(); openDrawer(); });
if (drawerClose) drawerClose.addEventListener('click', closeDrawer);
if (scrim) scrim.addEventListener('click', closeDrawer);
window.addEventListener('keydown', e => { if (e.key === 'Escape') closeDrawer(); });

if (drawerItems) {
  drawerItems.addEventListener('click', async e => {
    const btn = e.target.closest('[data-remove]');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    btn.disabled = true;
    // Clear the wallet BEFORE the mutation: a removal happens with the drawer open,
    // so during a slow (or failed) /cart/change.js the pre-change Apple Pay / Shop Pay
    // button must not stay tappable against a cart that's being emptied. Re-rendered
    // fresh on success; stays cleared on failure.
    // INVARIANT: this in-drawer remove is the ONLY cart mutation reachable while the
    // drawer is open — the active scrim (pointer-events:all, full viewport, below the
    // drawer) blocks the grid quick-add, PDP, and cart-page steppers. The other guards
    // (refresh() + openDrawer() clearing) cover only the "mutation started before open,
    // resolves after open" races. Together the set is closed.
    clearDrawerWallets();
    try {
      const cart = await changeLine(btn.dataset.remove, 0);
      renderCart(cart);
      renderDrawerWallets();
      window.dispatchEvent(new CustomEvent('bag:change', { detail: { cart } }));
    } catch (err) {
      console.error('[bag]', err);
      btn.disabled = false;
      showDrawerNote(err.message || 'The bag could not be updated. Try again.');
    }
  });
}

// Listen for add-to-cart form submits from elsewhere (product pages).
// Opt-in: only forms tagged [data-product-form] are AJAX-intercepted. The PDP
// express form (aa-express) posts to /cart/add too, but owns a dynamic-checkout
// submit that goes straight to checkout — intercepting it would silently
// add-to-cart instead of launching express. It carries no [data-product-form],
// so it passes through untouched.
// Guarded against double-submit; failures are surfaced on the submit button.
document.addEventListener('submit', async e => {
  const form = e.target;
  if (!form.matches || !form.matches('form[data-product-form]')) return;
  e.preventDefault();
  if (form.dataset.aaBusy === '1') return;
  form.dataset.aaBusy = '1';

  const submitBtn = form.querySelector('[type="submit"]');
  const originalText = submitBtn ? submitBtn.textContent : '';
  if (submitBtn) submitBtn.disabled = true;

  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      body: new FormData(form)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || err.message || 'Could not add to the bag. Try again.');
    }
    // The add succeeded; the drawer must reflect it. If the follow-up cart
    // read fails, retry once. The drawer only opens over CONFIRMED state:
    // stale contents behind a fresh add are worse than no drawer.
    let fresh = await refresh();
    if (!fresh) {
      await new Promise(r => setTimeout(r, 1000));
      fresh = await refresh();
    }
    if (navBag) {
      navBag.classList.add('just-added', 'has-items');
      setTimeout(() => navBag.classList.remove('just-added'), 600);
    }
    if (fresh) {
      openDrawer({ refresh: false });
    } else if (submitBtn) {
      // Added, but the bag state could not be confirmed for display.
      submitBtn.textContent = 'Added. Refresh to see your bag.';
      setTimeout(() => { submitBtn.textContent = originalText; }, 4000);
    }
  } catch (err) {
    console.error('[bag]', err);
    if (submitBtn) {
      const message = err.message && err.message.length <= 60 ? err.message : 'Could not add. Try again.';
      submitBtn.textContent = message;
      setTimeout(() => { submitBtn.textContent = originalText; }, 4000);
    }
  } finally {
    form.dataset.aaBusy = '';
    if (submitBtn) submitBtn.disabled = false;
  }
});

// Quick-add from collection grids (homepage prints). The express lane: one tap
// adds the single variant and slides the bag drawer in — deliberately NOT the
// 4.4s Acquire ritual, which stays on the product page for the deep read.
// Truth-gated like the ritual: the button only says "Acquired" after a real
// /cart/add.js 2xx; a failure shows a dismissible error and adds nothing.
// Strata-safe by construction: the button is only rendered server-side for
// cart-eligible (print / sculpture) single-variant works. That Liquid allowlist
// is the real guard — Shopify enforces inventory DENY at CHECKOUT, not at
// /cart/add.js — so register-gated originals never get the button at all.
document.addEventListener('click', async e => {
  const btn = e.target.closest('[data-quick-add]');
  if (!btn || btn.disabled) return;
  e.preventDefault();
  const variantId = btn.getAttribute('data-quick-add');
  if (!variantId || btn.dataset.aaBusy === '1') return;
  btn.dataset.aaBusy = '1';
  btn.disabled = true;
  const label = btn.querySelector('.aa26-cg-quickadd-label') || btn;
  const original = label.textContent;
  const restore = (text, ms) => {
    if (text != null) label.textContent = text;
    setTimeout(() => { label.textContent = original; btn.disabled = false; btn.dataset.aaBusy = ''; btn.classList.remove('is-added'); }, ms);
  };
  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: Number(variantId), quantity: 1 })
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || err.message || 'Could not add. Try again.');
    }
    label.textContent = 'Acquired ✓';
    btn.classList.add('is-added'); // morphs the mobile + into a tick (label is hidden on phones)
    // Mirror the form path: only open the drawer over CONFIRMED cart state.
    let fresh = await refresh();
    if (!fresh) { await new Promise(r => setTimeout(r, 1000)); fresh = await refresh(); }
    if (navBag) {
      navBag.classList.add('just-added', 'has-items');
      setTimeout(() => navBag.classList.remove('just-added'), 600);
    }
    if (fresh) openDrawer({ refresh: false });
    restore('Acquired ✓', 1600);
  } catch (err) {
    console.error('[quick-add]', err);
    restore((err.message && err.message.length <= 24) ? err.message : 'Try again', 2500);
  }
});

// Initial paint
refresh();

// Expose for external triggers
window.Bag = { refresh, open: openDrawer, close: closeDrawer };
